// src/pages/seeker/SeekerProfile.jsx
// CV list dashboard — shows all CV mockups, uploaded documents, create/upload buttons

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { FileText, MoreVertical, Star, Copy, Trash2, Upload, File, CheckCircle, Clock, AlertCircle, Loader } from 'lucide-react';
import { useConfirm, EditBtn, AddBtn, DelBtn } from '../../components/shared/UI';

const C = { navy: '#0e4e95', blue: '#3D80CC', white: '#ffffff', offwhite: '#f4f7fb', text: '#1a1a1a', muted: '#666', border: '#d0daea' };

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ParseBadge = ({ status }) => {
  const map = {
    PENDING:         { icon: <Clock size={11} />,       label: 'Pending',          bg: '#fff8e6', color: '#b45309', border: '#fcd34d' },
    PROCESSING:      { icon: <Loader size={11} />,      label: 'Parsing…',         bg: '#eff6ff', color: C.blue,    border: '#bfdbfe' },
    AWAITING_REVIEW: { icon: <AlertCircle size={11} />, label: 'Needs review',     bg: '#fff8e6', color: '#b45309', border: '#fcd34d' },
    REVIEWED:        { icon: <CheckCircle size={11} />, label: 'Applied',          bg: '#f0fdf4', color: '#15803d', border: '#86efac' },
    FAILED:          { icon: <AlertCircle size={11} />, label: 'Parse failed',     bg: '#fff1f2', color: '#be123c', border: '#fecdd3' },
  };
  const s = map[status] || map.PENDING;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {s.icon} {s.label}
    </span>
  );
};

// ── Card dropdown menu ─────────────────────────────────
const CardMenu = ({ cv, onMakeDefault, onDuplicate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const item = (onClick, icon, label, color, disabled) => (
    <button
      disabled={disabled}
      onClick={e => { e.stopPropagation(); setOpen(false); if (!disabled) onClick(); }}
      style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 14px', fontSize: '13px', fontWeight: 600, background: 'none', border: 'none', borderBottom: `1px solid ${C.border}`, cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? 'var(--text-muted)' : color, opacity: disabled ? 0.45 : 1 }}
    >
      {icon}{label}
    </button>
  );

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        style={{ background: open ? C.offwhite : 'none', border: `1px solid ${C.border}`, borderRadius: '3px', cursor: 'pointer', padding: '4px 5px', display: 'flex', alignItems: 'center', color: C.muted }}
      >
        <MoreVertical size={14} />
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200, background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 6px 20px rgba(0,0,0,0.12)', minWidth: '155px', overflow: 'hidden' }}>
          {item(() => onMakeDefault(cv.id), <Star size={13} />, 'Make Default', C.blue, cv.isDefault)}
          {item(() => onDuplicate(cv),      <Copy size={13} />, 'Duplicate',    C.text, false)}
          <button
            onClick={e => { e.stopPropagation(); setOpen(false); onDelete(cv.id); }}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 14px', fontSize: '13px', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}
          >
            <Trash2 size={13} /> Delete
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main page ──────────────────────────────────────────
export const SeekerProfile = () => {
  const { confirm, dialog } = useConfirm();
  const navigate = useNavigate();
  const [cvProfiles, setCVProfiles]   = useState([]);
  const [uploadedCVs, setUploadedCVs] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [creating, setCreating]       = useState(false);
  const [uploading, setUploading]     = useState(false);
  const uploadRef = useRef();

  const load = async () => {
    try {
      const [profilesRes, docsRes] = await Promise.all([
        seekerAPI.getCVProfiles(),
        seekerAPI.getCVs(),
      ]);
      setCVProfiles(profilesRes.data.data || []);
      setUploadedCVs(docsRes.data.data || []);
    } catch { toast.error('Failed to load CVs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // ── Upload a document CV ───────────────────────────
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('cv', file);
      await seekerAPI.uploadCV(form);
      toast.success('CV uploaded — parsing skills in the background…');
      await load();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteUploadedCV = async (id) => {
    if (!await confirm('Delete CV Document', 'This cannot be undone. Are you sure you want to delete this CV document?')) return;
    try {
      await seekerAPI.deleteCV(id);
      toast.success('CV deleted');
      await load();
    } catch { toast.error('Failed to delete CV'); }
  };

  const handleSetPrimary = async (id) => {
    try {
      await seekerAPI.setPrimaryCV(id);
      toast.success('Primary CV updated');
      await load();
    } catch { toast.error('Failed to update primary CV'); }
  };

  // ── CV Profile (builder) handlers ─────────────────
  const handleCreate = async () => {
    setCreating(true);
    try {
      await seekerAPI.createCVProfile({ title: 'New CV' });
      toast.success('CV created');
      await load();
    } catch { toast.error('Failed to create CV'); }
    finally { setCreating(false); }
  };

  const handleMakeDefault = async (id) => {
    try {
      await seekerAPI.updateCVProfile(id, { isDefault: true });
      await Promise.all(
        cvProfiles
          .filter(cv => cv.id !== id && cv.isDefault)
          .map(cv => seekerAPI.updateCVProfile(cv.id, { isDefault: false }))
      );
      toast.success('Default CV updated');
      await load();
    } catch { toast.error('Failed to update default'); }
  };

  const handleDuplicate = async (cv) => {
    try {
      const baseName = cv.title.replace(/ \(#\d+\)$/, '');
      const siblings = cvProfiles.filter(c => c.title.replace(/ \(#\d+\)$/, '') === baseName);
      const newTitle = `${baseName} (#${siblings.length + 1})`;
      await seekerAPI.createCVProfile({
        title:         newTitle,
        sectionConfig: cv.sectionConfig || {},
        styleConfig:   cv.styleConfig   || {},
      });
      toast.success(`Duplicated as "${newTitle}"`);
      await load();
    } catch { toast.error('Failed to duplicate CV'); }
  };

  const handleDelete = async (id) => {
    if (!await confirm('Delete CV', 'This cannot be undone. Are you sure you want to delete this CV?')) return;
    try {
      await seekerAPI.deleteCVProfile(id);
      toast.success('CV deleted');
      await load();
    } catch { toast.error('Failed to delete CV'); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div className="fade-in">
      {dialog}
      <div className="page-header">
        <h1>My CVs</h1>
        <p>Upload CV documents or build a CV profile</p>
      </div>

      {/* ── Uploaded CV Documents ───────────────────── */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: 700, color: C.navy, margin: 0 }}>Uploaded Documents</h2>
          {uploadedCVs.length === 0 ? (
            <button
              onClick={() => uploadRef.current.click()}
              disabled={uploading}
              className="btn btn-outline"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
            >
              <Upload size={14} /> {uploading ? 'Uploading…' : 'Upload CV'}
            </button>
          ) : (
            <span style={{ fontSize: '12px', color: C.muted }}>
              To re-upload, <a href="mailto:support@tikhet.com" style={{ color: C.blue }}>contact support</a>
            </span>
          )}
          <input
            ref={uploadRef}
            type="file"
            accept=".pdf,.doc,.docx"
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
        </div>

        {uploadedCVs.length === 0 ? (
          <div
            onClick={() => uploadRef.current.click()}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '32px 20px', background: C.white, borderRadius: '6px', border: `2px dashed ${C.border}`, cursor: 'pointer', color: C.muted }}
          >
            <Upload size={28} color={C.border} />
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: C.text }}>Upload your CV</p>
              <p style={{ margin: 0, fontSize: '12px' }}>PDF, DOC, or DOCX — up to 10 MB. Skills will be extracted automatically.</p>
            </div>
          </div>
        ) : (
          <div style={{ background: C.white, borderRadius: '6px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
            {uploadedCVs.map((doc, i) => (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
                <File size={20} color={C.blue} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }}>{doc.fileName}</span>
                    {doc.isPrimary && (
                      <span style={{ fontSize: '10px', fontWeight: 700, color: C.blue, background: '#e8eef5', padding: '2px 7px', borderRadius: '10px', border: `1px solid ${C.border}`, display: 'inline-flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                        <Star size={8} fill={C.blue} /> PRIMARY
                      </span>
                    )}
                    <ParseBadge status={doc.parseStatus} />
                  </div>
                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: C.muted }}>
                    {formatBytes(doc.fileSize)} · Uploaded {new Date(doc.createdAt).toLocaleDateString('en-AU')}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  {doc.parseStatus === 'AWAITING_REVIEW' && (
                    <button
                      onClick={() => navigate(`/seeker/cv-review/${doc.id}`)}
                      className="btn btn-primary btn-sm"
                      style={{ fontSize: '12px', padding: '4px 10px' }}
                    >
                      Review CV →
                    </button>
                  )}
                  {!doc.isPrimary && (
                    <button
                      onClick={() => handleSetPrimary(doc.id)}
                      className="btn btn-outline btn-sm"
                      style={{ fontSize: '12px', padding: '4px 10px' }}
                    >
                      Set Primary
                    </button>
                  )}
                  <DelBtn onClick={() => handleDeleteUploadedCV(doc.id)} size={14} title="Delete" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── CV Builder Profiles ─────────────────────── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: 700, color: C.navy, margin: 0 }}>CV Builder</h2>
          <AddBtn onClick={handleCreate} disabled={creating} size={14} style={{ fontSize: '13px' }} title={creating ? 'Creating…' : 'Create CV'} />
        </div>

        {cvProfiles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', background: C.white, borderRadius: '6px', border: `1px solid ${C.border}` }}>
            <FileText size={36} color={C.border} style={{ marginBottom: '12px' }} />
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: C.text, margin: '0 0 6px' }}>No CV profiles yet</h3>
            <p style={{ fontSize: '13px', color: C.muted, margin: '0 0 16px' }}>Build a tailored CV using your profile data</p>
            <AddBtn onClick={handleCreate} disabled={creating} size={14} title="Create CV" />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
            {cvProfiles.map(cv => (
              <div key={cv.id} style={{ background: C.white, border: `1px solid ${cv.isDefault ? C.blue : C.border}`, borderRadius: '6px', boxShadow: cv.isDefault ? '0 2px 12px rgba(61,128,204,0.18)' : '0 2px 8px rgba(14,78,149,0.08)', display: 'flex', flexDirection: 'column' }}>

                {/* Thumbnail */}
                <div style={{ background: C.navy, height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', borderRadius: '6px 6px 0 0' }}
                  onClick={() => navigate(`/seeker/profile/cv/${cv.id}`)}>
                  <FileText size={28} color="rgba(255,255,255,0.5)" />
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>CV MOCKUP</span>
                </div>

                {/* Body */}
                <div style={{ padding: '14px 16px', flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 700, color: C.text, margin: '0 0 5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cv.title}</h3>
                      {cv.isDefault && (
                        <span style={{ fontSize: '10px', fontWeight: 700, color: C.blue, background: '#e8eef5', padding: '2px 8px', borderRadius: '10px', border: `1px solid ${C.border}`, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <Star size={9} fill={C.blue} /> DEFAULT
                        </span>
                      )}
                    </div>
                    <CardMenu cv={cv} onMakeDefault={handleMakeDefault} onDuplicate={handleDuplicate} onDelete={handleDelete} />
                  </div>
                  <p style={{ fontSize: '11px', color: C.muted, margin: '8px 0 0' }}>
                    Created {new Date(cv.createdAt).toLocaleDateString('en-AU')}
                  </p>
                </div>

                {/* Footer */}
                <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.border}` }}>
                  <EditBtn onClick={() => navigate(`/seeker/profile/cv/${cv.id}`)} size={12} style={{ width: '100%', justifyContent: 'center' }} title="Edit" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
